/* ==========================================================================
   Hanlyn - small cross-page behaviours
   Created 2026-08-21.

   Companion to assets/hanlyn-ui-fixes.css. Loaded from layout/theme.liquid.
   Keep each behaviour small, commented, and defensive - it runs on every page.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Product page accordions.

     The PDP renders each section as a native <details> inside
     .product__accordion. Two behaviours are added:

       1. The first accordion (Product Details) starts open, so the key copy is
          visible without a click.
       2. Opening one closes the others - one panel at a time.

     Native <details> has no grouping behaviour of its own, so the exclusivity
     is done by listening for `toggle` and closing siblings. The guard flag
     stops the programmatic closes from re-entering this handler.
     ---------------------------------------------------------------------- */
  var closingOthers = false;

  function productAccordions() {
    var container = document.querySelector('.product__info-container');
    if (!container) return;

    var panels = [].slice.call(container.querySelectorAll('.product__accordion details'));
    if (!panels.length) return;

    // 1. Open the first one, unless the page already has one open (e.g. a
    //    theme-editor preview or a browser restoring state).
    var anyOpen = panels.some(function (d) {
      return d.open;
    });
    if (!anyOpen) panels[0].open = true;

    // 2. One at a time.
    panels.forEach(function (panel) {
      if (panel.dataset.hanlynAccordionBound === 'true') return;
      panel.dataset.hanlynAccordionBound = 'true';

      panel.addEventListener('toggle', function () {
        if (closingOthers) return;
        if (!panel.open) return;

        closingOthers = true;
        panels.forEach(function (other) {
          if (other !== panel) other.open = false;
        });
        closingOthers = false;
      });
    });
  }

  /* ------------------------------------------------------------------------
     Sezzle in a non-USD market.
     Added 2026-08-28.

     Sezzle is a third-party payment provider. Shopify only offers third-party
     providers when the checkout currency matches the store's default currency,
     which is USD:
     https://help.shopify.com/en/manual/international/pricing/limitations

     So a shopper in the Canada market (CAD) never sees Sezzle at checkout, and
     verified order history confirms it: every Sezzle order the store has ever
     taken was USD. The Sezzle Checkout Button app embed still renders for those
     shoppers though, so clicking it lands them on a checkout offering only
     Credit card and PayPal. That dead end is what this fixes.

     What it does, for shoppers whose active currency is not USD:

       1. Labels Sezzle's checkout button so the currency switch is an informed
          choice rather than a surprise.
       2. Intercepts the click and sends them to /cart?country=US, which flips
          them into the USD market. The cart survives because both markets now
          share www.hanlyncollective.com. Sezzle then appears at checkout.
       3. Shows a confirmation banner on the USD cart.

     Shoppers already in USD are untouched. The guard is a runtime currency
     check, not a Liquid one, so a cached section cannot leave a stale state.

     To remove: delete this block. Nothing else references it.
     ---------------------------------------------------------------------- */

  // The store's default currency. Sezzle is only offered in this currency.
  var SEZZLE_CURRENCY = 'USD';

  // The country whose market uses SEZZLE_CURRENCY.
  var SEZZLE_COUNTRY = 'US';

  /* Anything Sezzle injects. Deliberately broad: the app embed renders client
     side and its markup is not ours to pin down. The only action taken on a
     match is a redirect to the USD cart, so a false positive is harmless. */
  var SEZZLE_SELECTOR = [
    '.sezzle-checkout-button',
    '[class*="sezzle-checkout"]',
    '[id*="sezzle-checkout"]',
    '[class*="sezzle-button"]',
    '[id*="sezzle-button"]',
    '[data-sezzle-checkout]',
    'sezzle-checkout-button'
  ].join(',');

  var sezzleClickBound = false;
  var sezzleObserver = null;

  function activeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || SEZZLE_CURRENCY;
  }

  function sezzleStyles() {
    if (document.getElementById('hanlyn-sezzle-styles')) return;

    var style = document.createElement('style');
    style.id = 'hanlyn-sezzle-styles';
    style.textContent = [
      '.hanlyn-sezzle-note{display:block;margin:0.6rem 0 0;font-size:1.2rem;',
      'line-height:1.4;letter-spacing:0.04rem;text-align:center;',
      'color:rgba(var(--color-foreground),0.7);}',
      '.hanlyn-sezzle-banner{margin:0 0 1.6rem;padding:1.2rem 1.6rem;',
      'border:1px solid rgba(var(--color-foreground),0.15);',
      'background:rgba(var(--color-foreground),0.04);font-size:1.4rem;',
      'line-height:1.5;text-align:center;}'
    ].join('');
    document.head.appendChild(style);
  }

  /* Confirmation banner, shown once they land back on the cart in USD. */
  function sezzleBanner() {
    if (window.location.search.indexOf('sezzle=usd') === -1) return;

    var host = document.querySelector('.cart__footer, .cart-drawer__footer');
    if (!host || host.querySelector('.hanlyn-sezzle-banner')) return;

    sezzleStyles();

    var el = document.createElement('div');
    el.className = 'hanlyn-sezzle-banner';
    el.innerHTML =
      '<strong>Now showing US dollars.</strong> Choose Sezzle as your payment ' +
      'method at checkout. To go back to Canadian dollars, use the country ' +
      'selector at the bottom of any page.';
    host.insertBefore(el, host.firstChild);
  }

  /* Label the checkout button, but not the instalment messaging widget on
     product pages - that one stays as it is. */
  function sezzleLabels() {
    var nodes = document.querySelectorAll(SEZZLE_SELECTOR);
    if (!nodes.length) return;

    sezzleStyles();

    [].slice.call(nodes).forEach(function (el) {
      if (el.dataset.hanlynSezzleLabelled === 'true') return;
      el.dataset.hanlynSezzleLabelled = 'true';

      var looksLikeButton =
        el.tagName === 'BUTTON' ||
        el.tagName === 'A' ||
        /checkout/i.test(el.id || '') ||
        /checkout/i.test(typeof el.className === 'string' ? el.className : '');
      if (!looksLikeButton) return;
      if (!el.parentNode) return;
      if (el.parentNode.querySelector('.hanlyn-sezzle-note')) return;

      var note = document.createElement('small');
      note.className = 'hanlyn-sezzle-note';
      note.textContent =
        'Sezzle is billed in US dollars. Choosing it switches your cart to USD.';
      el.parentNode.insertBefore(note, el.nextSibling);
    });
  }

  /* Capture phase, so this runs before Sezzle's own handler. */
  function onSezzleClick(event) {
    if (activeCurrency() === SEZZLE_CURRENCY) return;

    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest(SEZZLE_SELECTOR)) return;

    event.preventDefault();
    event.stopPropagation();
    window.location.href = '/cart?country=' + SEZZLE_COUNTRY + '&sezzle=usd';
  }

  function sezzleUsdSwitch() {
    if (activeCurrency() === SEZZLE_CURRENCY) {
      sezzleBanner();
      return;
    }

    if (!sezzleClickBound) {
      document.addEventListener('click', onSezzleClick, true);
      sezzleClickBound = true;
    }

    sezzleLabels();

    if (!sezzleObserver) {
      sezzleObserver = new MutationObserver(function () {
        sezzleLabels();
      });
      sezzleObserver.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  function init() {
    productAccordions();
    sezzleUsdSwitch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', init);
})();
